import React, { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddBranchModal from './AddBranchModal';
import EditBranchModal from './EditBranchModal';

interface Branch {
  branch_id: string;
  branch_code?: string;
  institute_id: string;
  name: string;
  license_number: string;
  license_start: string;
  license_expiry: string;
  state?: string;
  district?: string;
  village?: string;
  branch_type?: string;
  address: string;
  pincode: number;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  active_operator: {
    user_id: string;
    name: string;
    email: string;
    assigned_at: string;
  } | null;
  created_at: string;
}

interface StudentCount {
  branch_id: string;
  institute_id: string;
  total: number;
  active: number;
  inactive: number;
  enrolled: number;
}

interface ApiResponse {
  success: boolean;
  data: Branch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface StudentCountApiResponse {
  success: boolean;
  data: StudentCount;
}

interface BranchesProps {
  onBranchClick?: (branchId: string) => void;
}

const BRANCH_TYPE_LABELS: Record<string, string> = {
  asc: 'ASC',
  franchise: 'Franchise',
  main: 'Main Branch',
};

const getBranchTypeLabel = (type?: string) =>
  type ? (BRANCH_TYPE_LABELS[type] ?? type) : null;

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy:     '#14267A',
  navyMid:  '#2238A0',
  navyDeep: '#0B1650',
  gold:     '#F5B326',
  page:     '#F4F5FA',
  tint:     '#E8EBF8',
  tintSoft: '#F4F5FA',
  line:     '#E2E5F2',
  lineStrong: '#DADFF0',
  text:     '#12183A',
  textSoft: '#5A6080',
};

// ── Skeleton shimmer for student count ────────────────────────────────────────
const CountSkeleton: React.FC = () => (
  <div style={{ display: 'flex', gap: '6px' }}>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          flex: 1,
          height: '40px',
          borderRadius: '10px',
          background: 'linear-gradient(90deg, #eceef7 25%, #dde1f1 50%, #eceef7 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
        }}
      />
    ))}
    <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
  </div>
);

// ── Student count tiles (Total / Active / Enrolled) ────────────────────────────
const StudentCountRow: React.FC<{ count: StudentCount | null; loading: boolean; hov?: boolean }> = ({
  count,
  loading,
  hov = false,
}) => {
  if (loading) return <CountSkeleton />;
  if (!count) return null;

  const tiles = [
    { label: 'Total',    value: count.total },
    { label: 'Active',   value: count.active },
    { label: 'Enrolled', value: count.enrolled },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' }}>
      {tiles.map(({ label, value }) => (
        <div
          key={label}
          style={{
            padding: '7px 10px',
            background: hov ? 'white' : C.tintSoft,
            borderRadius: '10px',
          }}
        >
          <div style={{ fontSize: '0.95rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>{value}</div>
          <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: C.textSoft }}>
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── License status → label + colours ──────────────────────────────────────────
const getLicenseInfo = (expiry: string) => {
  const exp = new Date(expiry).getTime();
  if (isNaN(exp)) return { label: 'No expiry date', bg: '#EEF0F8', fg: '#1B2A72', days: Infinity };
  const days = Math.ceil((exp - Date.now()) / 86400000);
  const date = new Date(exp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  if (days < 0)   return { label: `Expired ${date}`,        bg: '#FDE3E3', fg: '#9B1C1C', days };
  if (days <= 60) return { label: `Expires in ${days} days`, bg: '#FFE9BF', fg: '#8A4B00', days };
  return { label: `Valid till ${date}`, bg: '#EEF0F8', fg: '#1B2A72', days };
};

// ── Main component ─────────────────────────────────────────────────────────────
const Branches: React.FC<BranchesProps> = ({ onBranchClick }) => {
  const toast    = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});

  const [branches, setBranches]             = useState<Branch[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [search, setSearch]                 = useState('');
  const [page, setPage]                     = useState(1);
  const [pagination, setPagination]         = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showEditModal, setShowEditModal]   = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [hoveredId, setHoveredId]           = useState<string | null>(null); // card turns super light blue on hover
  const [deleteDialog, setDeleteDialog]     = useState<{
    visible: boolean;
    branchId: string;
    branchName: string;
  }>({ visible: false, branchId: '', branchName: '' });

  // Student counts keyed by branch_id
  const [studentCounts, setStudentCounts]   = useState<Record<string, StudentCount>>({});
  const [countsLoading, setCountsLoading]   = useState<Record<string, boolean>>({});

  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } =
    useSuperAdminDashboardStore();

  useEffect(() => {
    if (!dashboardData) fetchDashboardData();
  }, [dashboardData, fetchDashboardData]);

  // Inject PrimeReact style overrides once
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .p-menu { border-radius: 12px !important; border: 1px solid #DADFF0 !important; box-shadow: 0 8px 24px rgba(20,38,122,0.14) !important; padding: 6px !important; }
      .p-menu .p-menuitem-link { border-radius: 8px !important; padding: 9px 12px !important; margin: 2px 0 !important; transition: all 0.2s ease !important; }
      .p-menu .p-menuitem-link:hover { background: #EEF0F8 !important; }
      .p-menu .p-menuitem-icon { color: #14267A !important; font-size: 0.85rem !important; }
      .p-menu .p-menuitem-text { color: #12183A !important; font-weight: 600 !important; font-size: 0.8rem !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => { fetchBranches(); }, [page, search]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search }),
      });
      const response = await apiClient.get<ApiResponse>(`/v1/branches?${params}`);
      if (response.data.success) {
        setBranches(response.data.data);
        setPagination(response.data.pagination);
        fetchAllStudentCounts(response.data.data);
      } else {
        setError('Failed to load branches');
      }
    } catch (err: any) {
      console.error('Failed to fetch branches:', err);
      setError(err.response?.data?.error || 'Failed to load branches');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load branches',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch student counts for all branches in parallel.
   * Endpoint: GET /v1/students/branch-student-count?branch_id=<id>
   * Response: { success: boolean; data: StudentCount }
   */
  const fetchAllStudentCounts = async (branchList: Branch[]) => {
    // Mark all branches as loading
    const initialLoading: Record<string, boolean> = {};
    branchList.forEach((b) => { initialLoading[b.branch_id] = true; });
    setCountsLoading(initialLoading);

    const results = await Promise.allSettled(
      branchList.map((b) =>
        apiClient
          .get<StudentCountApiResponse>(
            `/v1/students/branch-student-count?branch_id=${b.branch_id}`
          )
          .then((res) => {
            if (!res.data.success) throw new Error('API returned success: false');
            return res.data.data;
          })
      )
    );

    const newCounts: Record<string, StudentCount>  = {};
    const newLoading: Record<string, boolean>      = {};

    branchList.forEach((b, i) => {
      const result = results[i];
      if (result.status === 'fulfilled') {
        newCounts[b.branch_id] = result.value;
      }
      // fulfilled OR rejected — either way we're done loading
      newLoading[b.branch_id] = false;
    });

    setStudentCounts((prev) => ({ ...prev, ...newCounts }));
    setCountsLoading((prev) => ({ ...prev, ...newLoading }));
  };

  const handleAddBranchSuccess  = () => { fetchBranches(); };
  const handleEditBranchSuccess = () => { fetchBranches(); };
  const handleEditClick         = (branch: Branch) => {
    setSelectedBranch(branch);
    setShowEditModal(true);
  };

  const handleDeleteBranch = (branchId: string, branchName: string) => {
    setDeleteDialog({ visible: true, branchId, branchName });
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/branches/${deleteDialog.branchId}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Branch deleted successfully',
        life: 3000,
      });
      fetchBranches();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.error || 'Failed to delete branch',
        life: 4000,
      });
    } finally {
      setDeleteDialog({ visible: false, branchId: '', branchName: '' });
    }
  };

  const getMenuItems = (branch: Branch) => [
    {
      label: 'View Details',
      icon: 'pi pi-eye',
      command: () => onBranchClick && onBranchClick(branch.branch_id),
    },
    { label: 'Edit',   icon: 'pi pi-pencil', command: () => handleEditClick(branch) },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => handleDeleteBranch(branch.branch_id, branch.name),
    },
  ];

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // ── Guards ──────────────────────────────────────────────────────────────────

  if (dashboardLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" />
        <p style={{ color: C.navy, fontSize: '0.85rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message
          severity="warn"
          text="Unable to load dashboard data. Please refresh the page."
          style={{ fontSize: '0.85rem' }}
        />
      </div>
    );
  }

  if (loading && branches.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.page, borderRadius: '18px' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.85rem' }}>Loading branches...</p>
        </div>
      </div>
    );
  }

  // ── Summary numbers (based on the branches loaded on this page) ─────────────
  const activeCount   = branches.filter((b) => b.status === 'active').length;
  const studentsTotal = branches.reduce((sum, b) => sum + (studentCounts[b.branch_id]?.total ?? 0), 0);
  const expiringCount = branches.filter((b) => { const d = getLicenseInfo(b.license_expiry).days; return d >= 0 && d <= 60; }).length;

  const stats = [
    { icon: 'pi-building',             label: 'Total branches',   value: pagination.total, tag: 'All',       iconBg: C.tint,     iconFg: C.navy,    tagBg: '#EEF0F8', tagFg: '#4A5070', cardBg: 'white',   cardBorder: C.line },
    { icon: 'pi-check-circle',         label: 'Active branches',  value: activeCount,      tag: 'This page', iconBg: '#E3F6C4',  iconFg: '#2C5A12', tagBg: '#EEF0F8', tagFg: '#4A5070', cardBg: 'white',   cardBorder: C.line },
    { icon: 'pi-users',                label: 'Students',         value: studentsTotal,    tag: 'This page', iconBg: C.tint,     iconFg: C.navy,    tagBg: '#EEF0F8', tagFg: '#4A5070', cardBg: 'white',   cardBorder: C.line },
    { icon: 'pi-exclamation-circle',   label: 'Licenses expiring', value: expiringCount,   tag: '60 days',   iconBg: '#FFE9BF',  iconFg: '#8A4B00', tagBg: '#FFE9BF', tagFg: '#8A4B00', cardBg: '#FFF8EA', cardBorder: '#F6E2B8' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '0', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text }}>
      <Toast ref={toast} />
      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, branchId: '', branchName: '' })}
        onConfirm={confirmDelete}
        title="Delete Branch"
        message={`Are you sure you want to delete "${deleteDialog.branchName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Toolbar (page title + breadcrumb already come from the app layout) ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
          <div style={{ position: 'relative', width: '360px', maxWidth: '100%' }}>
            <i className="pi pi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: C.textSoft, fontSize: '0.85rem' }}></i>
            <InputText
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, branch ID, state, district..."
              style={{ width: '100%', paddingLeft: '40px', height: '40px', border: `1px solid ${C.lineStrong}`, borderRadius: '12px', fontSize: '0.8rem', background: 'white' }}
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', height: '40px', padding: '0 20px',
              borderRadius: '12px', border: 'none', background: C.navy, color: 'white',
              fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 10px 22px rgba(20,38,122,0.28)', transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.navyDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; }}
          >
            <i className="pi pi-plus" style={{ fontSize: '0.85rem' }}></i>
            Add Branch
          </button>
        </div>
      </div>

      {/* ── Summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {stats.map((st) => (
          <div key={st.label} style={{ background: st.cardBg, border: `1px solid ${st.cardBorder}`, borderRadius: '16px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: st.iconBg, color: st.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`pi ${st.icon}`} style={{ fontSize: '0.95rem' }}></i>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.4px', lineHeight: 1.1 }}>{st.value}</div>
              <div style={{ fontSize: '0.7rem', color: C.textSoft, marginTop: '2px' }}>{st.label}</div>
            </div>
            <span style={{ padding: '2px 8px', borderRadius: '999px', background: st.tagBg, color: st.tagFg, fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{st.tag}</span>
          </div>
        ))}
      </div>

      {error && (
        <Message
          severity="error"
          text={error}
          style={{ marginBottom: '16px', fontSize: '0.85rem', fontWeight: 500 }}
        />
      )}

      {/* ── Grid ── */}
      {branches.length === 0 && !loading ? (
        <div style={{ padding: '70px 20px', textAlign: 'center', background: 'white', borderRadius: '18px', border: `2px dashed ${C.lineStrong}` }}>
          <i className="pi pi-building" style={{ fontSize: '2.5rem', color: '#C6CDE8', marginBottom: '14px' }}></i>
          <h3 style={{ margin: '0 0 8px 0', color: C.textSoft, fontSize: '1rem', fontWeight: 700 }}>
            No branches found
          </h3>
          <p style={{ margin: 0, color: '#9298B5', fontSize: '0.85rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first branch'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
          {branches.map((branch) => {
            const hov = hoveredId === branch.branch_id; // white by default, super light blue on hover
            const lic = getLicenseInfo(branch.license_expiry);
            const count = studentCounts[branch.branch_id] ?? null;
            const fill = count && branch.capacity > 0 ? Math.min(100, Math.round((count.total / branch.capacity) * 100)) : 0;
            const isActive = branch.status === 'active';

            const textMain  = C.text;
            const textSoftC = C.textSoft;
            const chipBg    = hov ? 'white' : C.tintSoft;
            const chipIcon  = C.navy;

            return (
              <div
                key={branch.branch_id}
                style={{
                  position: 'relative', overflow: 'hidden',
                  background: hov ? '#EEF3FF' : 'white',
                  color: textMain,
                  border: `1px solid ${hov ? '#C6D4FF' : C.line}`,
                  borderRadius: '18px', padding: '14px',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                  boxShadow: hov ? '0 10px 24px rgba(20,38,122,0.10)' : '0 6px 18px rgba(20,38,122,0.05)',
                  transform: hov ? 'scale(1.02)' : 'scale(1)',
                  transition: 'background-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
                }}
                onMouseEnter={() => setHoveredId(branch.branch_id)}
                onMouseLeave={() => setHoveredId(null)}
              >

                {/* Title row */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '11px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', fontWeight: 800, flexShrink: 0 }}>
                    {branch.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, letterSpacing: '-0.2px', color: textMain }}>{branch.name}</h3>
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                      {branch.branch_code && (
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: C.textSoft }}>{branch.branch_code}</span>
                      )}
                      {branch.branch_type && (
                        <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#EEF0F8', color: '#4A5070', fontSize: '0.62rem', fontWeight: 700 }}>
                          {getBranchTypeLabel(branch.branch_type)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{
                    display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '999px',
                    fontSize: '0.68rem', fontWeight: 700, textTransform: 'capitalize', flexShrink: 0,
                    background: isActive ? ('#E3F6C4') : '#FDE3E3',
                    color: isActive ? ('#2C5A12') : '#9B1C1C',
                  }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }}></span>
                    {branch.status}
                  </span>
                </div>

                {/* Location */}
                {(branch.district || branch.state) && (
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.75rem', color: textSoftC }}>
                    <i className="pi pi-map-marker" style={{ fontSize: '0.75rem' }}></i>
                    {[branch.district, branch.state].filter(Boolean).join(', ')}
                  </div>
                )}

                {/* Capacity bar */}
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.7rem', color: textSoftC }}>Capacity used</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700 }}>
                      <span style={{ fontSize: '0.88rem', letterSpacing: '-0.3px' }}>{count ? count.total : '–'}</span>{' '}
                      <span style={{ color: textSoftC, fontWeight: 500 }}>/ {branch.capacity} students</span>
                    </span>
                  </div>
                  <div style={{ height: '6px', borderRadius: '999px', background: C.tint, overflow: 'hidden' }}>
                    <div style={{ width: `${fill}%`, height: '100%', borderRadius: '999px', background: `linear-gradient(90deg, ${C.navyMid}, ${C.navy})`, transition: 'width 0.4s ease' }} />
                  </div>
                </div>

                {/* Student counts */}
                <div style={{ position: 'relative' }}>
                  <StudentCountRow count={count} loading={countsLoading[branch.branch_id] ?? false} hov={hov} />
                </div>

                {/* Info chips: PCs + license */}
                <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {branch.total_pc > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 9px', borderRadius: '9px', background: chipBg, fontSize: '0.7rem', fontWeight: 600, color: textMain }}>
                      <i className="pi pi-desktop" style={{ fontSize: '0.75rem', color: chipIcon }}></i>{branch.total_pc} PCs
                    </span>
                  )}
                  {branch.opening_time && branch.closing_time && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 9px', borderRadius: '9px', background: chipBg, fontSize: '0.7rem', fontWeight: 600, color: textMain }}>
                      <i className="pi pi-clock" style={{ fontSize: '0.75rem', color: chipIcon }}></i>{branch.opening_time} – {branch.closing_time}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 9px', borderRadius: '9px', background: lic.bg, fontSize: '0.7rem', fontWeight: 700, color: lic.fg }}>
                    <i className="pi pi-id-card" style={{ fontSize: '0.75rem', color: 'inherit' }}></i>{lic.label}
                  </span>
                </div>
                <div style={{ position: 'relative', fontSize: '0.66rem', color: textSoftC, fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>
                  License {branch.license_number}
                </div>

                {/* Operator */}
                <div style={{ position: 'relative' }}>
                  {branch.active_operator ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px', background: chipBg, borderRadius: '11px' }}>
                      <Avatar
                        label={branch.active_operator.name.charAt(0)}
                        shape="circle"
                        style={{ backgroundColor: C.navy, color: C.gold, width: '30px', height: '30px', fontSize: '0.75rem', fontWeight: 800 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700 }}>{branch.active_operator.name}</div>
                        <div style={{ fontSize: '0.68rem', color: textSoftC, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {branch.active_operator.email}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.62rem', fontWeight: 600, color: textSoftC }}>Operator</span>
                    </div>
                  ) : (
                    <div style={{ padding: '9px', borderRadius: '11px', border: `1.5px dashed ${'#C6CDE8'}`, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: textSoftC }}>
                      No operator assigned
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ position: 'relative', display: 'flex', gap: '8px' }}>
                  <button
                    style={{
                      flex: 1, height: '34px', borderRadius: '11px', border: 'none',
                      background: C.navy, color: 'white',
                      fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                    }}
                    onClick={() => onBranchClick && onBranchClick(branch.branch_id)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.navyDeep; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; }}
                  >
                    <i className="pi pi-eye" style={{ fontSize: '0.85rem' }}></i>
                    View Details
                  </button>
                  <button
                    aria-label="More actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      menuRefs.current[branch.branch_id]?.toggle(e);
                    }}
                    style={{
                      width: '34px', height: '34px', borderRadius: '11px',
                      background: 'white',
                      border: `1px solid ${C.lineStrong}`,
                      color: C.navy, cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <i className="pi pi-ellipsis-v" style={{ fontSize: '0.85rem' }}></i>
                  </button>
                  <Menu
                    model={getMenuItems(branch)}
                    popup
                    ref={(el) => { menuRefs.current[branch.branch_id] = el; }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: '12px', marginTop: '20px', padding: '12px 16px',
          background: 'white', borderRadius: '16px', border: `1px solid ${C.line}`,
        }}>
          <span style={{ fontSize: '0.78rem', color: C.textSoft }}>
            Page <strong style={{ color: C.text }}>{pagination.page}</strong> of <strong style={{ color: C.text }}>{pagination.pages}</strong>
            {' · '}<strong style={{ color: C.text }}>{pagination.total}</strong> branches
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
              style={{
                width: '36px', height: '36px', borderRadius: '11px',
                background: page === 1 ? C.tintSoft : C.navy,
                border: 'none', color: page === 1 ? '#9298B5' : 'white',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <i className="pi pi-chevron-left" style={{ fontSize: '0.75rem' }}></i>
            </button>
            <button
              disabled={page === pagination.pages}
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              aria-label="Next page"
              style={{
                width: '36px', height: '36px', borderRadius: '11px',
                background: page === pagination.pages ? C.tintSoft : C.navy,
                border: 'none', color: page === pagination.pages ? '#9298B5' : 'white',
                cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
              }}
            >
              <i className="pi pi-chevron-right" style={{ fontSize: '0.75rem' }}></i>
            </button>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <AddBranchModal
        visible={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleAddBranchSuccess}
        toastRef={toast}
      />
      <EditBranchModal
        visible={showEditModal}
        branch={selectedBranch}
        onHide={() => { setShowEditModal(false); setSelectedBranch(null); }}
        onSuccess={handleEditBranchSuccess}
        toastRef={toast}
      />
    </div>
  );
};

export default Branches;